import defu from 'defu'
import { state } from '../state'
import { getNodeSelector } from '../browser'
import { getCssSelector } from '../utils/dom-selector'
import { isSensitiveFormField } from '../utils/redact-form'

// =========================
// Section: track function
// =========================

/**
 * Track an event with attributes. Dispatches an `EventPayload` (Faro's
 * `EventEvent` shape) through the EventBus.
 *
 * @param event - The event name.
 * @param attributes - The event attributes.
 * @example
 * ```ts
 * track('click', { button_text: 'Click me' })
 * ```
 */
export async function track(event: string, attributes: Record<string, unknown> = {}): Promise<void> {
  await state.eventBus.dispatch({
    name: event,
    domain: state.source,
    attributes,
    timestamp: new Date().toISOString(),
  })
}

// =========================
// Section: trackLink function
// =========================

const trackedLinks = new WeakSet<HTMLAnchorElement>()

// Types for the possible arguments of the trackLink function
// Accepts: (link), (link, properties), (links), (links, properties)
type TrackLinksArgs =
  | [HTMLAnchorElement, Record<string, string>?]
  | [HTMLAnchorElement[], Record<string, string>?]

/**
 * Track a link interaction with properties.
 * @param link - The link element.
 * @param properties - The properties to track.
 */
export function trackLink(link: HTMLAnchorElement, properties?: Record<string, string>): Promise<void>;
export function trackLink(links: HTMLAnchorElement[], properties?: Record<string, string>): Promise<void>;
export async function trackLink(...args: TrackLinksArgs): Promise<void> {
  const [linkOrLinks, properties] = args

  if (Array.isArray(linkOrLinks)) {
    return linkOrLinks.forEach(link => {
      trackLink(link, properties)
    })
  }

  const link = linkOrLinks;

  if (trackedLinks.has(link)) return
  trackedLinks.add(link)

  const handleTrack = async (eventType: string) => {
    const linkData = {
      link_href: link.href,
      link_id: link.id,
      link_text: link.textContent ? link.textContent.replace(/[\n\t]/g, '').trim() : '',
    }
    const dataMontillaAttrs = getDataMontillaAttrs(link)
    const eventProperties = getValidProperties(linkData, dataMontillaAttrs, properties ?? {}, { link_event: eventType })
    await track('link_interaction', eventProperties)
  }

  link.addEventListener('click', () => handleTrack('click'))
  link.addEventListener('auxclick', () => handleTrack('auxclick'))
  link.addEventListener('contextmenu', () => handleTrack('contextmenu'))
}

// =========================
// Section: trackSubmit function
// =========================

type TrackSubmitArgs =
  | [SubmitEvent, string, Record<string, string>?]
  | [SubmitEvent, Record<string, string>?]

interface ParsedTrackSubmitArgs {
  submitEvent: SubmitEvent;
  eventName: string;
  properties: Record<string, string>;
}

export function trackSubmit(submitEvent: SubmitEvent, properties?: Record<string, string>): Promise<void>;
export function trackSubmit(submitEvent: SubmitEvent, eventName: string, properties?: Record<string, string>): Promise<void>;
export async function trackSubmit(...args: TrackSubmitArgs): Promise<void> {
  const { submitEvent, eventName, properties } = parseTrackSubmitArgs(...args)

  const form = submitEvent.target

  if (!(form instanceof HTMLFormElement)) {
    return
  }

  const formData: Record<string, string> = {
    form_action: form.action,
    form_id: form.id ? form.id : getNodeSelector(form),
  }

  const dataMontillaAttrs = getDataMontillaAttrs(form)
  const submittedData: Record<string, string> = {}
  // Field NAMES whose value was dropped (passwords, card data). Reported as one
  // property so a redaction firing on a form that should hold no card data is
  // visible — a silent drop is indistinguishable from the field not existing.
  const redactedFields: string[] = []

  const fd = new FormData(form)
  for (const [key, value] of fd.entries()) {
    if (typeof value === 'string') {
      // Capturing every field is the intended default; card data is the
      // exception (see `isSensitiveFormField`). Redaction drops the value —
      // never masks it — so no PAN reaches the payload.
      if (isSensitiveFormField(form.elements.namedItem(key), key, value)) {
        redactedFields.push(key)
        continue
      }
      submittedData['data_' + key] = value
    }
  }

  if (redactedFields.length > 0) {
    formData.form_redacted_fields = redactedFields.join(',')
  }

  const eventProperties = getValidProperties(formData, dataMontillaAttrs, submittedData, properties ?? {}, { form_event: eventName })

  track('submit', eventProperties)
}

function parseTrackSubmitArgs(...args: TrackSubmitArgs): ParsedTrackSubmitArgs {
  if (typeof args[1] === 'string') {
    return {
      submitEvent: args[0],
      eventName: args[1],
      properties: args[2] ?? {},
    }
  }
  return {
    submitEvent: args[0],
    eventName: 'submit',
    properties: args[1] ?? {},
  }
}

// =========================
// Section: trackForm function
// =========================

const trackedForms = new WeakSet<HTMLFormElement>()

type TrackFormsArgs =
  | [HTMLFormElement, string, Record<string, string>?]
  | [HTMLFormElement[], string, Record<string, string>?]

export function trackForm(form: HTMLFormElement, event: string, properties?: Record<string, string>): Promise<void>;
export function trackForm(forms: HTMLFormElement[], event: string, properties?: Record<string, string>): Promise<void>;
export async function trackForm(...args: TrackFormsArgs): Promise<void> {
  const [formOrForms, eventName, properties] = args

  if (Array.isArray(formOrForms)) {
    return formOrForms.forEach(form => {
      trackForm(form, eventName, properties)
    })
  }

  const form = formOrForms;

  if (trackedForms.has(form)) return
  trackedForms.add(form)

  form.addEventListener('submit', (e) => {
    trackSubmit(e, eventName, properties)
  })
}

// =========================
// Section: trackElement function
// =========================

const trackedElements = new WeakSet<Element>()

type TrackElementsArgs =
  | [Element, string, Record<string, string>?]
  | [Element[], string, Record<string, string>?]
  | [Element, string[], Record<string, string>?]
  | [Element[], string[], Record<string, string>?]

export function trackElement(element: Element, event: string, properties?: Record<string, string>): Promise<void>;
export function trackElement(elements: Element[], event: string, properties?: Record<string, string>): Promise<void>;
export function trackElement(element: Element, events: string[], properties?: Record<string, string>): Promise<void>;
export function trackElement(elements: Element[], events: string[], properties?: Record<string, string>): Promise<void>;
export async function trackElement(...args: TrackElementsArgs): Promise<void> {
  const [elementOrElements, eventOrEvents, properties] = args

  if (Array.isArray(elementOrElements)) {
    if (Array.isArray(eventOrEvents)) {
      const events = eventOrEvents;
      return elementOrElements.forEach(element => {
        trackElement(element, events, properties)
      })
    }
    const event = eventOrEvents;
    return elementOrElements.forEach(element => {
      trackElement(element, event, properties)
    })
  }

  const element = elementOrElements;

  if (trackedElements.has(element)) return
  trackedElements.add(element)

  const handleTrack = async (eventType: string) => {
    const elementData = {
      element_id: element.id,
      element_type: element.tagName,
      element_text: element.textContent ? element.textContent.replace(/[\n\t]/g, '').trim() : '',
      element_label: getAccessibleName(element),
      element_classes: element.getAttribute('class') ?? '',
      element_selector: getCssSelector(element),
    }
    const dataMontillaAttrs = getDataMontillaAttrs(element)
    const eventProperties = getValidProperties(elementData, dataMontillaAttrs, properties ?? {}, { element_event: eventType })
    await track(eventType, eventProperties)
  }

  if (Array.isArray(eventOrEvents)) {
    const events = eventOrEvents;
    return events.forEach(event => {
      element.addEventListener(event, () => handleTrack(event))
    })
  }

  const event = eventOrEvents;
  element.addEventListener(event, () => handleTrack(event))
}


// --- private functions ---

export function getDataMontillaAttrs(element: Element): Record<string, string> {
  const dataMontillaAttrs: Record<string, string> = {}
  Array.from(element.attributes).forEach(attr => {
    if (attr.name.startsWith('data-montilla')) {
      dataMontillaAttrs[attr.name] = attr.value
    }
  })
  return dataMontillaAttrs
}

/**
 * Resolve an element's accessible name — useful for icon-only buttons/links
 * whose visible text is empty. Follows the ARIA precedence that matters here:
 * `aria-label` → `aria-labelledby` (text of referenced nodes) → `title`.
 * Returns a trimmed string (empty when none apply → filtered by
 * getValidProperties).
 */
export function getAccessibleName(element: Element): string {
  const ariaLabel = element.getAttribute('aria-label')
  if (ariaLabel?.trim()) return ariaLabel.trim()

  const labelledBy = element.getAttribute('aria-labelledby')
  if (labelledBy) {
    const text = labelledBy
      .split(/\s+/)
      .map(id => element.ownerDocument?.getElementById(id)?.textContent ?? '')
      .join(' ')
      .replace(/[\n\t]/g, '')
      .trim()
    if (text) return text
  }

  const title = element.getAttribute('title')
  return title?.trim() ?? ''
}

export function getValidProperties(...args: Record<string, string>[]) {
  const validAttrs = args.map(arg => Object.fromEntries(Object.entries(arg).filter(([, value]) => value)))
  return defu({}, ...validAttrs)
}
